import { Injectable, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Meta } from '@angular/platform-browser';

/**
 * GfMetaService — the single authority for per-page SEO metadata.
 *
 * ## Responsibilities
 *
 * 1. Set the meta description via Angular's Meta service.
 * 2. Manage the canonical `<link rel="canonical">` element.
 * 3. Set Open Graph (og:*) tags for social previews.
 * 4. Inject JSON-LD structured data for search engines.
 *
 * It mirrors the DI-friendly style of GfTitleService: it is provided in
 * CoreServicesModule and injected into components by constructor.
 */
@Injectable()
export class GfMetaService {

    static readonly CANONICAL_SELECTOR = 'link[rel="canonical"]';
    static readonly JSONLD_SELECTOR = 'script[type="application/ld+json"][data-gf-jsonld]';

    constructor(
        @Inject(DOCUMENT) private doc: any,
        private meta: Meta
    ) {}

    /**
     * Set the meta description tag. Empty/null values are ignored so a
     * missing description never clobbers an existing one.
     */
    setDescription(description: string): void {
        const value = this.normalize(description);
        if (!value) return;
        this.meta.updateTag({ name: 'description', content: value });
    }

    /**
     * Set the canonical URL for the current page. Creates the
     * `<link rel="canonical">` element if it does not exist yet.
     */
    setCanonicalUrl(url: string): void {
        const value = this.normalize(url);
        if (!value) return;
        let link = this.doc.querySelector(GfMetaService.CANONICAL_SELECTOR);
        if (!link) {
            link = this.doc.createElement('link');
            link.setAttribute('rel', 'canonical');
            this.doc.head.appendChild(link);
        }
        link.setAttribute('href', value);
    }

    /**
     * Set an Open Graph tag (e.g. og:title, og:description, og:image).
     * Empty/null values are ignored.
     */
    setOgTag(property: string, content: string): void {
        const value = this.normalize(content);
        if (!value) return;
        this.meta.updateTag({ property: property, content: value });
    }

    /**
     * Set the standard Open Graph tags for a page in one call.
     */
    setOpenGraph(opts: { title?: string; description?: string; image?: string; url?: string }): void {
        if (opts.title) this.setOgTag('og:title', opts.title);
        if (opts.description) this.setOgTag('og:description', opts.description);
        if (opts.image) this.setOgTag('og:image', opts.image);
        if (opts.url) this.setOgTag('og:url', opts.url);
    }

    /**
     * Inject a JSON-LD structured-data block. Any previously injected
     * block (marked with data-gf-jsonld) is replaced so navigation between
     * pages does not accumulate stale structured data.
     */
    setJsonLd(data: object): void {
        const existing = this.doc.querySelector(GfMetaService.JSONLD_SELECTOR);
        if (existing) {
            existing.parentNode.removeChild(existing);
        }
        const script = this.doc.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-gf-jsonld', '');
        script.text = JSON.stringify(data);
        this.doc.head.appendChild(script);
    }

    /**
     * Coerce an arbitrary value into a trimmed string, guarding against
     * nullish and non-string values from API payloads.
     */
    private normalize(raw: any): string {
        if (raw === null || raw === undefined) return '';
        if (typeof raw === 'string') return raw.trim();
        if (typeof raw === 'number' || typeof raw === 'boolean') {
            return `${raw}`.trim();
        }
        return '';
    }
}
